import { useMemo, useState } from 'react';
import { Edit2, Plus, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { LoadingPage } from '@/components/ui/loading';
import { Modal } from '@/components/ui/modal';
import { SortToolbar } from '@/components/ui/sort-toolbar';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import {
  createSaltUnitType,
  updateSaltUnitType,
  type SaltUnitType,
} from '../api/salt-unit-types-api';
import { invalidateSaltUnitTypesCache, useSaltUnitTypes } from '../hooks/use-salt-unit-types';

// Owner-only admin page (May 14 batch). CRUD for the SALT unit-of-measure
// lookup table that drives the device modal's dropdown. WireCode is the
// device-side identity and is immutable post-create; DisplayName + IsActive
// are editable. System-seeded entries (gallons/liters/ft3/m3) can be
// deactivated but never deleted from the UI — soft warning + proceed is
// the May 14 batch's resolved policy.
//
// May 15 batch adds the Gateways-style top toolbar: search by display
// name OR wire code, sort dropdown + sortable column headers sharing
// the same state. Filter + sort are client-side over the
// already-loaded list (handful of rows total; no need to round-trip).

type SaltSortKey = 'displayName' | 'wireCode' | 'isActive' | 'createdAt';
type SortDir = 'asc' | 'desc';

export function SaltUnitTypesPage() {
  const { data, loading, error, refetch } = useSaltUnitTypes(false);

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<SaltUnitType | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<SaltUnitType | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Top toolbar state
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState<SaltSortKey>('displayName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Create form state
  const [newWireCode, setNewWireCode] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');

  // Edit form state
  const [editDisplayName, setEditDisplayName] = useState('');

  // Search + sort applied to the loaded list. Search is case-insensitive
  // against display name AND wire code (per brainstorm decision).
  const visibleRows = useMemo(() => {
    if (!data) return [];
    const q = searchInput.trim().toLowerCase();
    const filtered = q
      ? data.filter((r) =>
          r.displayName.toLowerCase().includes(q) ||
          r.wireCode.toLowerCase().includes(q))
      : data;
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'displayName':
          cmp = a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
          break;
        case 'wireCode':
          cmp = a.wireCode.localeCompare(b.wireCode, undefined, { sensitivity: 'base' });
          break;
        case 'isActive':
          cmp = Number(b.isActive) - Number(a.isActive); // active first asc
          break;
        case 'createdAt':
          cmp = a.createdAt.localeCompare(b.createdAt);
          break;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [data, searchInput, sortBy, sortDir]);

  function handleSort(nextSortBy: SaltSortKey) {
    if (sortBy === nextSortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(nextSortBy);
      setSortDir('asc');
    }
  }

  if (loading && data === null) return <LoadingPage />;

  async function handleCreate() {
    if (!newWireCode.trim() || !newDisplayName.trim()) {
      setSubmitError('Wire code and display name are required.');
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const res = await createSaltUnitType({
        wireCode: newWireCode.trim(),
        displayName: newDisplayName.trim(),
      });
      if (!res.success) {
        setSubmitError(res.message ?? 'Create failed.');
        return;
      }
      invalidateSaltUnitTypesCache();
      refetch();
      setShowCreate(false);
      setNewWireCode('');
      setNewDisplayName('');
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message || 'Create failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSaveEdit() {
    if (!editing) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const res = await updateSaltUnitType(editing.id, { displayName: editDisplayName.trim() });
      if (!res.success) {
        setSubmitError(res.message ?? 'Update failed.');
        return;
      }
      invalidateSaltUnitTypesCache();
      refetch();
      setEditing(null);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message || 'Update failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDeactivate() {
    if (!confirmDeactivate) return;
    const target = confirmDeactivate;
    setConfirmDeactivate(null);
    setIsSubmitting(true);
    try {
      await updateSaltUnitType(target.id, { isActive: !target.isActive });
      invalidateSaltUnitTypesCache();
      refetch();
    } catch {
      // No-op surface; the table will reload and show the unchanged state.
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">SALT Unit Types</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage the unit-of-measure dropdown shown to installers when configuring SALT meters. Renaming a display name cascades to every device that uses it.
            </p>
          </div>
          <Button
            className="flex-shrink-0 whitespace-nowrap"
            onClick={() => { setNewWireCode(''); setNewDisplayName(''); setSubmitError(null); setShowCreate(true); }}
          >
            <Plus className="w-4 h-4" />
            Add unit type
          </Button>
        </div>

        {error && (
          <Card variant="elevated">
            <p className="p-4 text-sm text-error-600">{error}</p>
          </Card>
        )}

        {/* Search + Sort toolbar — Gateways-style top affordance.
            Wraps to a second row on narrow viewports. */}
        {data && data.length > 0 && (
          <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by display name or wire code..."
                className="pl-9"
              />
            </div>
            <SortToolbar
              sortBy={sortBy}
              sortDir={sortDir}
              onSortByChange={setSortBy}
              onSortDirChange={setSortDir}
              options={[
                { value: 'displayName', label: 'Display name' },
                { value: 'wireCode', label: 'Wire code' },
                { value: 'isActive', label: 'Status' },
                { value: 'createdAt', label: 'Created' },
              ]}
            />
          </div>
        )}

        {data && data.length === 0 ? (
          <Card variant="elevated">
            <EmptyState
              icon={<Plus className="w-12 h-12" />}
              title="No unit types yet"
              description="Add a unit type to populate the SALT device dropdown."
            />
          </Card>
        ) : visibleRows.length === 0 ? (
          <Card variant="elevated">
            <EmptyState
              title="No unit types match"
              description={`No display name or wire code matches "${searchInput}".`}
              action={{ label: 'Clear search', onClick: () => setSearchInput('') }}
            />
          </Card>
        ) : (
          <Card variant="elevated" padding="none">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    label="Display Name"
                    active={sortBy === 'displayName'}
                    direction={sortDir}
                    onClick={() => handleSort('displayName')}
                  />
                  <SortableTableHead
                    label="Wire Code"
                    active={sortBy === 'wireCode'}
                    direction={sortDir}
                    onClick={() => handleSort('wireCode')}
                  />
                  <SortableTableHead
                    label="Status"
                    active={sortBy === 'isActive'}
                    direction={sortDir}
                    onClick={() => handleSort('isActive')}
                  />
                  <TableHead className="text-right">Devices</TableHead>
                  <SortableTableHead
                    label="Created"
                    active={sortBy === 'createdAt'}
                    direction={sortDir}
                    onClick={() => handleSort('createdAt')}
                  />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      {row.displayName}
                      {row.isSystem && (
                        <Badge variant="info" size="sm" className="ml-2">System</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-gray-500 dark:text-gray-400">{row.wireCode}</TableCell>
                    <TableCell>
                      <Badge variant={row.isActive ? 'success' : 'default'} size="sm" dot>
                        {row.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.deviceCount}</TableCell>
                    <TableCell className="text-xs text-gray-500 dark:text-gray-400">{formatDate(row.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setEditing(row); setEditDisplayName(row.displayName); setSubmitError(null); }}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDeactivate(row)}
                      >
                        {row.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Create */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add Unit Type" size="sm">
        <div className="space-y-4">
          <Input
            label="Wire Code"
            value={newWireCode}
            onChange={(e) => setNewWireCode(e.target.value)}
            placeholder="e.g., lb"
            helperText="The identifier stored on each device. Letters, digits, underscore, hyphen. Immutable after create."
            maxLength={16}
            required
          />
          <Input
            label="Display Name"
            value={newDisplayName}
            onChange={(e) => setNewDisplayName(e.target.value)}
            placeholder="e.g., Pounds"
            helperText="The label shown in the SALT device dropdown and in reports."
            maxLength={64}
            required
          />
          {submitError && <p className="text-sm text-error-600">{submitError}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} isLoading={isSubmitting}>Add unit type</Button>
          </div>
        </div>
      </Modal>

      {/* Edit display name */}
      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title="Edit Unit Type" size="sm">
        <div className="space-y-4">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Wire Code (immutable)</p>
            <p className="font-mono text-sm text-gray-700 dark:text-gray-300 mt-1">{editing?.wireCode}</p>
          </div>
          <Input
            label="Display Name"
            value={editDisplayName}
            onChange={(e) => setEditDisplayName(e.target.value)}
            maxLength={64}
            required
          />
          {submitError && <p className="text-sm text-error-600">{submitError}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} isLoading={isSubmitting}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* Deactivate / Activate confirm */}
      <ConfirmDialog
        isOpen={!!confirmDeactivate}
        title={confirmDeactivate?.isActive ? 'Deactivate Unit Type' : 'Activate Unit Type'}
        description={
          confirmDeactivate?.isActive ? (
            <>
              <p>
                Deactivate <strong>{confirmDeactivate.displayName}</strong>?
              </p>
              {confirmDeactivate.deviceCount > 0 ? (
                <p className="text-gray-700 dark:text-gray-300 mt-2">
                  <strong>{confirmDeactivate.deviceCount}</strong> device{confirmDeactivate.deviceCount === 1 ? '' : 's'} currently use{confirmDeactivate.deviceCount === 1 ? 's' : ''} this unit type. They will continue to display the label but new devices won&apos;t be able to select it.
                </p>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 mt-2">
                  No devices currently use this unit type.
                </p>
              )}
            </>
          ) : (
            <p>Re-activate <strong>{confirmDeactivate?.displayName}</strong>? It will appear in the SALT device dropdown again.</p>
          )
        }
        confirmLabel={confirmDeactivate?.isActive ? 'Deactivate' : 'Activate'}
        onConfirm={handleDeactivate}
        onCancel={() => setConfirmDeactivate(null)}
      />
    </div>
  );
}
